import { FontPairing, PairingCategory } from '../pairings-data'

const BASE_URL = '/api/v1/font-pairing-categories'
const PAIRINGS_URL = '/api/v1/font-pairings'

interface ApiPairing {
  id: number
  display_font: string
  body_font: string
  display_weight: string
  body_weight: string
  name: string | null
  description: string | null
  ai_model: string
  sort_order: number
  favorites_count: number
}

interface ApiCategory {
  id: number
  slug: string
  name: string
  description: string | null
  emoji: string | null
  sort_order: number
  pairings_count: number
  pairings: ApiPairing[]
}

function transformPairing(raw: ApiPairing, categorySlug: string, index: number): FontPairing {
  return {
    id: `${categorySlug}-${index + 1}`,
    numericId: raw.id,
    displayFont: raw.display_font,
    bodyFont: raw.body_font,
    name: raw.name ?? undefined,
    favoritesCount: raw.favorites_count,
  }
}

export interface PairingCategoryWithNumericId extends PairingCategory {
  numericId: number
}

function transformCategory(raw: ApiCategory): PairingCategory {
  return {
    id: raw.slug,
    name: raw.name,
    description: raw.description ?? '',
    emoji: raw.emoji ?? '',
    pairings: raw.pairings.map((p, i) => transformPairing(p, raw.slug, i)),
  }
}

function transformCategoryWithId(raw: ApiCategory): PairingCategoryWithNumericId {
  return {
    ...transformCategory(raw),
    numericId: raw.id,
  }
}

interface CreatePairingParams {
  display_font: string
  body_font: string
  display_weight?: string
  body_weight?: string
  name?: string
  description?: string
  font_pairing_category_id: number
}

export const pairingsApi = {
  async listCategories(aiModel?: string): Promise<PairingCategory[]> {
    const params = aiModel ? `?ai_model=${aiModel}` : ''
    const res = await fetch(`${BASE_URL}.json${params}`)
    if (!res.ok) throw new Error(`API error ${res.status}`)
    const data: ApiCategory[] = await res.json()
    return data.map(transformCategory)
  },

  async getCategory(slug: string, aiModel?: string): Promise<PairingCategory | null> {
    const params = aiModel ? `?ai_model=${aiModel}` : ''
    const res = await fetch(`${BASE_URL}/${slug}.json${params}`)
    if (res.status === 404) return null
    if (!res.ok) throw new Error(`API error ${res.status}`)
    const data: ApiCategory = await res.json()
    return transformCategory(data)
  },

  async listCategoriesWithIds(aiModel?: string): Promise<PairingCategoryWithNumericId[]> {
    const params = aiModel ? `?ai_model=${aiModel}` : ''
    const res = await fetch(`${BASE_URL}.json${params}`)
    if (!res.ok) throw new Error(`API error ${res.status}`)
    const data: ApiCategory[] = await res.json()
    return data.map(transformCategoryWithId)
  },

  async createPairing(params: CreatePairingParams): Promise<void> {
    const csrfMeta = document.querySelector('meta[name="csrf-token"]')
    const csrfToken = csrfMeta?.getAttribute('content') || ''

    const res = await fetch(`${PAIRINGS_URL}.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      credentials: 'same-origin',
      body: JSON.stringify({ font_pairing: params }),
    })
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) {
        throw new Error('sign_in_required')
      }
      const data = await res.json().catch(() => ({}))
      throw new Error(data.errors ? JSON.stringify(data.errors) : `API error ${res.status}`)
    }
  },
}
